import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import ImageManager from '../../misc/ImageManager';

import Tooltip from '../../misc/Tooltip';
import { isEmptyValue } from '../../../lib/validation';
import FieldTypes from '../../../constants/FieldTypes';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}

`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right:0;
  top:0;
  z-index:99
`;

const StyledImageManagerWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  border: 1px solid transparent;

`;

const ImageField = ({

  fieldId,
  fieldIdentifier,
  fieldPositionId,

  title,
  value,
  multi,
  enableSystemFilePicker,

  fontColor,
  padding,
  margin,
  borderRadius,
  borderWidth,
  borderColor,
  backgroundColor,
  primaryColor,
  secondaryColor,
  dangerColor,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  disabled,
  isMobileView,

  primaryMaxWidth,
  primaryMaxHeight,
  primaryDisplayOnly,

  onChange,
  onFocus,

  onFileUpload,
  onFileClick,
  onFileDelete,

  tipTitle,
  tipDescription,
  tipVisible,

  //Used for calculating width and height of element
  measure,
  onMeasureCompleted,

  type,
  inputGroupEmbedded,

  minHeight,

  //Used for calculating width and height of element
  measuredHeight,
  measuredOn,
  updatedOn,

}) => {

  const isDisabled = primaryDisplayOnly || disabled;

  const values = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter((v) => v);
    }
    return value ? [value] : [];
  }, [value]);


  const [wrapperRef, setWrapperRef] = useState(null);
  const [loadedImages, setLoadedImages] = useState(values.length < 1);
  const [multiple, setMultiple] = useState(multi);

  /**
   * Handle Field Measurement
   */
  const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);
  const hasPopulatedValue = !isEmptyValue(values, FieldTypes.image);

  useEffect(() => {

    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    /**
     * We only measure the image field for the following scenarios:
     * 
     * 1. When image field is independent field, set to multi, and has a populated value. This is to ensure that single image fields are not resized 
     * and can properly contain a single image use-case.
     * 2. When image field is embedded in an input group. This is to ensure that the image field always allows the 
     * input group row to finish measuring. This is embedded flag is unique to input groups use-case.
     */
    const imageFieldMeasure = onMeasureCompleted && wrapperRef && multi && loadedImages && hasPopulatedValue && measure && refHeightAvailable && remeasureRequired;
    const inputGroupImageFieldMeasure = inputGroupEmbedded && onMeasureCompleted && measure && loadedImages;
    
    if (imageFieldMeasure || inputGroupImageFieldMeasure) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {height: refHeightAvailable, measuredOn: new Date().getTime()});
      });
    }

  }, [wrapperRef, loadedImages, remeasureRequired, measure, values]);

  useEffect(() => {
    if (Array.isArray(value) && value.length > 1) {
      setMultiple(true);
    }
  }, [value]);

  /**
   * Handle file upload
   * 
   * If the system file picker is disabled then files will be undefined.
   * 
   * @param {*} loadingCallback(boolean) //Handles displaying and hiding loading indicators
   * @param {*} errorCallback(error) //Handles displaying error indicator
   * @param {*} files? //Files selected from the file system dioalog. This is an option argument
   */
  const handleUpload = useCallback((loadingCallback, errorCallback, files) => {
    setLoadedImages(false);
    if (onFileUpload) onFileUpload(fieldId, fieldIdentifier, fieldPositionId, files, loadingCallback, errorCallback);
  }, []);

  const handleClick = useCallback((i, urlObject) => {
    if (onFileClick) onFileClick(fieldId, fieldIdentifier, fieldPositionId, urlObject, i);
  }, []);

  const handleDelete = useCallback((i, urlObject) => {
    const nextImageValues = [...values];
    nextImageValues.splice(i, 1);

    /**
     * Important Note: onFileDelete is simply to notify about the deletion event. The actual
     * removal of the image is handled by onChange.
     */
    if (onFileDelete) onFileDelete(fieldId, fieldIdentifier, fieldPositionId, urlObject, i);
    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextImageValues);

  }, [values]);

  const handleFocus = useCallback(() => {
    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});
  }, []);

  const handleLoadCompleted = useCallback(() => {
    setLoadedImages(true);
  }, []);

  return (
    <StyledWrapper
      {...{
        margin,
        measure: measure && hasPopulatedValue && multi
      }}
      ref={(e) => {
        if (e && !wrapperRef) setWrapperRef(e);
      }}
    >
      <FieldTitle
        {...{
          title,
          titleDisplay,
          titleFontSize,
          titleFontColor,
          titleTextAlign,
          titleFontStyle,
          titleFontWeight,
          titleTextTransform,
          titleTextDecoration,
        }}
      />
      {disabled ? null : (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      )}
      <StyledImageManagerWrapper {...{ borderRadius, disabled: isDisabled }}>
        <ImageManager
          {...{
            type,
            value,
            isMobileView,
            multiple: multi,
            disabled: isDisabled,
            enableSystemFilePicker,
            inputGroupEmbedded,

            onLoadCompleted: handleLoadCompleted,

            onDelete: handleDelete,
            onUpload: handleUpload,
            onFocus: handleFocus,
            onClick: handleClick,

            maxWidth: primaryMaxWidth ? `${primaryMaxWidth}px` : 0,
            maxHeight: primaryMaxHeight ? `${primaryMaxHeight}px` : 0,
            fontColor,
            padding,
            borderRadius,
            borderWidth,
            borderColor,
            backgroundColor,
            primaryColor,
            secondaryColor,
            dangerColor,
            minHeight,
          }}
        />
      </StyledImageManagerWrapper>
    </StyledWrapper>
  );

};

export default React.memo(ImageField);
